<template>
  <div class="left-behind-indicators" :style="containerStyle">
    <div
      v-for="(item, index) in indicatorsByDate"
      :key="index"
      v-tooltip="{
        content: getTooltipHTML(item),
        html: true,
        placement: 'auto',
        classes: 'otp-tooltip',
        trigger: 'hover',
      }"
      class="indicator-dot"
      :style="getDotStyle(item)"
    >
      {{ item.count }}
    </div>
  </div>
</template>

<script>
import { generateLeftBehindTooltipHTML } from '../utils/otpFormatters';

export default {
  name: 'LeftBehindIndicators',
  props: {
    data: {
      type: Array,
      required: true,
    },
    chartData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chartWidth: 0,
      chartLeftPadding: 0,
      chartRightPadding: 0,
      plotBottomOffset: 0,
    };
  },
  computed: {
    /**
     * One disc per date, carrying that date's total.
     *
     * `data` is a flat list of left-behind events — a row per route and stop — and several
     * rows share a date. Every row is drawn at the centre of its date's category, so rows
     * of the same date landed exactly on top of each other: the strip showed one disc per
     * date reading whichever event happened to be drawn last (usually 1), while the chart
     * legend counted them all. Summing per date puts the two back in agreement, and the
     * events are kept so the tooltip can break the day down (VSER-14676).
     */
    indicatorsByDate() {
      const byDate = new Map();

      // Grouped by the chart bucket (hour, day, week or month), which keeps what the tooltip
      // needs to name it.
      this.data.forEach((event) => {
        const { key, date, hour, period, start, end } = event;
        const entry = byDate.get(key) || { key, date, hour, period, start, end, count: 0, events: [] };
        entry.count += event.count || 0;
        entry.events.push(event);
        byDate.set(key, entry);
      });

      return [...byDate.values()];
    },
    containerStyle() {
      // Position the indicator strip just below the plot area (x-axis line),
      // in the gap between the axis and the date labels. Uses a fallback
      // until the plot area dimensions are measured.
      const fallbackBottom = 160;
      const bottom = this.plotBottomOffset > 0 ? this.plotBottomOffset : fallbackBottom;
      return { bottom: `${bottom}px` };
    },
  },
  watch: {
    chartData: {
      handler() {
        this.$nextTick(() => {
          this.calculateChartDimensions();
        });
      },
      deep: true,
    },
  },
  mounted() {
    // Delay calculation to ensure Highcharts is fully rendered
    // Use longer delay and retry mechanism
    this.initializeChartDimensions();
    window.addEventListener('resize', this.calculateChartDimensions);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateChartDimensions);
  },
  methods: {
    initializeChartDimensions() {
      // Try to calculate dimensions with retry mechanism
      let attempts = 0;
      const maxAttempts = 10;
      const retryDelay = 200;

      const tryCalculate = () => {
        attempts += 1;
        const success = this.calculateChartDimensions();

        if (!success && attempts < maxAttempts) {
          setTimeout(tryCalculate, retryDelay);
        }
      };

      setTimeout(tryCalculate, 300);
    },
    calculateChartDimensions() {
      // The positioning context is the parent chart-container (position: relative)
      const parentContainer = this.$el?.parentElement;
      if (!parentContainer) return false;

      const plotArea = parentContainer.querySelector('.highcharts-plot-background');
      if (!plotArea) return false;

      const rect = plotArea.getBoundingClientRect();
      const parentRect = parentContainer.getBoundingClientRect();

      this.chartWidth = rect.width;
      this.chartLeftPadding = rect.left - parentRect.left;
      this.chartRightPadding = parentRect.right - rect.right;
      // Distance from the chart-container bottom to the plot area bottom (x-axis line).
      // This is where the indicator strip should sit — right below the axis line,
      // in the gap before the date labels.
      this.plotBottomOffset = parentRect.bottom - rect.bottom - 24;

      return this.chartWidth > 0;
    },
    getDotStyle(item) {
      if (!this.chartData || this.chartData.length === 0 || this.chartWidth === 0) {
        return { left: '0px', bottom: '0px' };
      }

      // Find the index of this bucket in the chart data
      const dateIndex = this.chartData.findIndex((d) => d.key === item.key);

      if (dateIndex === -1) {
        return { left: '0px', bottom: '0px' };
      }

      // Calculate position based on category index
      // Highcharts uses discrete categories, so each date has equal spacing
      const totalCategories = this.chartData.length;
      const categoryWidth = this.chartWidth / totalCategories;

      // Position at the center of the category
      const categoryPosition = dateIndex + 0.5;
      const pixelPosition = this.chartLeftPadding + categoryPosition * categoryWidth;

      return {
        left: `${pixelPosition}px`,
        bottom: '0px',
      };
    },
    getTooltipHTML(item) {
      return generateLeftBehindTooltipHTML(item, this.$t.bind(this));
    },
  },
};
</script>

<style scoped>
.left-behind-indicators {
  position: absolute;
  left: 0;
  right: 0;
  height: 20px;
  pointer-events: none;
}

/* One fixed size for every day: the count is read off the disc, not off its area. */
.indicator-dot {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background-color: #af52de;
  border-radius: 50%;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  pointer-events: all;
  transition: transform 0.2s;
  transform: translateX(-50%);
}

.indicator-dot:hover {
  transform: translateX(-50%) scale(1.2);
}
</style>
